/**
 * JSON diff utility — compute unified diff giữa 2 JSON values cho audit viewer.
 *
 * Output format:
 *   { path: string[]; kind: "added" | "removed" | "changed"; before?: any; after?: any }[]
 *
 * Path là array of keys/indexes — vd `["fields", 2, "label"]` cho fields[2].label.
 */

export type DiffKind = "added" | "removed" | "changed";

export interface DiffEntry {
  path: (string | number)[];
  kind: DiffKind;
  before?: unknown;
  after?: unknown;
}

export function diffJson(
  before: unknown,
  after: unknown,
  path: (string | number)[] = [],
): DiffEntry[] {
  if (deepEqual(before, after)) {
    return [];
  }
  // Both objects → recurse keys
  if (
    isPlainObject(before) &&
    isPlainObject(after)
  ) {
    const out: DiffEntry[] = [];
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    for (const k of [...keys].sort()) {
      const b = (before as Record<string, unknown>)[k];
      const a = (after as Record<string, unknown>)[k];
      if (b === undefined && a !== undefined) {
        out.push({ path: [...path, k], kind: "added", after: a });
      } else if (a === undefined && b !== undefined) {
        out.push({ path: [...path, k], kind: "removed", before: b });
      } else if (!deepEqual(b, a)) {
        out.push(...diffJson(b, a, [...path, k]));
      }
    }
    return out;
  }
  // Both arrays → diff by index (simple — not LCS)
  if (Array.isArray(before) && Array.isArray(after)) {
    const out: DiffEntry[] = [];
    const max = Math.max(before.length, after.length);
    for (let i = 0; i < max; i++) {
      const b = i < before.length ? before[i] : undefined;
      const a = i < after.length ? after[i] : undefined;
      if (b === undefined && a !== undefined) {
        out.push({ path: [...path, i], kind: "added", after: a });
      } else if (a === undefined && b !== undefined) {
        out.push({ path: [...path, i], kind: "removed", before: b });
      } else if (!deepEqual(b, a)) {
        out.push(...diffJson(b, a, [...path, i]));
      }
    }
    return out;
  }
  // Scalar / mismatched type — single change
  return [{ path: [...path], kind: "changed", before, after }];
}

export function formatPath(path: (string | number)[]): string {
  if (path.length === 0) return "(root)";
  return path
    .map((p, i) =>
      typeof p === "number" ? `[${p}]` : i === 0 ? p : `.${p}`,
    )
    .join("");
}

export function formatValue(v: unknown): string {
  if (v === undefined) return "(none)";
  if (v === null) return "null";
  if (typeof v === "string") return JSON.stringify(v);
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    const json = JSON.stringify(v, null, 2);
    return json.length > 200 ? json.slice(0, 200) + "..." : json;
  } catch {
    return String(v);
  }
}

// ============================================================================
// Helpers
// ============================================================================
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((x, i) => deepEqual(x, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) =>
      deepEqual(
        (a as Record<string, unknown>)[k],
        (b as Record<string, unknown>)[k],
      ),
    );
  }
  return false;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return (
    typeof v === "object" &&
    v !== null &&
    !Array.isArray(v) &&
    Object.getPrototypeOf(v) === Object.prototype
  );
}
